import * as z from "zod";

export const PublicationStatusSchema = z.enum(["draft", "published"]);

// Base schema with common fields
const BaseContentSchema = z.object({
  id: z.uuid().describe("Unique identifier"),
  name: z.string().min(1).describe("The official or common name"),
  description: z.string().optional().describe("Text overview or details"),
  metadata: z
    .record(z.string(), z.unknown())
    .nullable()
    .optional()
    .describe("Additional contextual fields (e.g., tags)"),
  status:
    PublicationStatusSchema.default("draft").describe("Publication status"),
  created_by: z.string().nullable().optional().default(null),
  created_at: z.iso.datetime().describe("Timestamp of creation"),
  updated_at: z.iso.datetime().describe("Timestamp of last update"),
  published_at: z
    .string()
    .datetime()
    .optional()
    .describe("Timestamp of publication"),
});

// POI Type Schema
export const POITypeSchema = z
  .enum([
    "parking",
    "toilet",
    "entrance",
    "viewpoint",
    "water_source",
    "shelter",
    "camping",
    "rescue_point",
    "information",
    "other",
  ])
  .describe("Type of point of interest");

export const ImageSchema = z
  .object({
    id: z.string().uuid().describe("Unique identifier extracted from filename"),
    original: z.object({
      url: z.string().url(),
    }),
    base_path: z.string().optional(),
    sizes: z
      .record(
        z.enum(["large", "medium", "small", "thumb"]),
        z.object({
          width: z.number(),
          height: z.number(),
        })
      )
      .optional(),
    available_formats: z.array(z.enum(["avif", "webp", "jpeg"])).optional(),
  })
  .describe("Image with variants information");

// Lenient image schema for search results - allows any structure with original.url
export const SearchImageSchema = z
  .object({
    id: z.string().optional(),
    original: z
      .object({
        url: z.string(),
      })
      .optional(),
    base_path: z.string().optional(),
    sizes: z.record(z.string(), z.unknown()).optional(),
    available_formats: z.array(z.string()).optional(),
  })
  .passthrough()
  .describe("Image for search results - lenient validation");

// Point of Interest Schema
export const PointOfInterestSchema = z
  .object({
    id: z.uuid().describe("Unique identifier for the POI"),
    name: z.string().min(1).describe("Name or description of the POI"),
    type: POITypeSchema.describe("Type of point of interest"),
    coordinates: z
      .tuple([z.number(), z.number()])
      .describe("[longitude, latitude]"),
    description: z
      .string()
      .optional()
      .describe("Additional details about the POI"),
  })
  .describe("A point of interest at an area");

// Area Schema - the top-level entity in the hierarchy
export const AreaSchema = BaseContentSchema.extend({
  slug: z.string().min(1).describe("URL-friendly identifier"),
  points_of_interest: z
    .array(PointOfInterestSchema)
    .optional()
    .describe("Array of points of interest at an area"),
  images: z
    .array(ImageSchema)
    .nullable()
    .describe("Collection of images with variants"),

  area_map_data: z
    .union([z.record(z.string(), z.unknown()), z.string(), z.null()])
    .optional()
    .describe(
      "GeoJSON FeatureCollection containing waypoints and tracks for the area"
    ),
  team_id: z
    .string()
    .uuid()
    .nullable()
    .optional()
    .describe(
      "Team managing this area - if set, all team members can edit the area"
    ),
}).describe("Represents a climbing area - the top-level geographic entity");

// Model Schema - base schema without refinement
const BaseModelSchema = BaseContentSchema.extend({
  area_id: z.string().uuid().describe("Foreign key linking to the parent Area"),
  slug: z.string().min(1).describe("URL-friendly identifier"),
  coordinates: z
    .object({
      // Example: Using GeoJSON Point structure
      type: z.literal("Point"),
      coordinates: z
        .tuple([z.number(), z.number()])
        .describe("[longitude, latitude]"),
    })
    .nullable()
    .optional()
    .describe("Precise geospatial point data for the model"),
  model_reference_3d: z
    .object({
      original: z
        .object({
          url: z.url(),
          size: z.number(),
          sizeReadable: z.string(),
        })
        .optional(),
      optimized: z
        .object({
          url: z.url(),
          size: z.number(),
          sizeReadable: z.string(),
          compressionRatio: z.number(),
        })
        .optional(),
      variants: z.record(z.string(), z.unknown()).optional(), // Allow any shape for variants
    })
    .nullable()
    .optional()
    .describe(
      "3D model references with original optional (excluded for bandwidth optimization), optimized optional"
    ),
  images: z
    .array(ImageSchema)
    .nullable()
    .optional()
    .describe("Visual aids (images/videos)"),

  // Models are typically published directly or in preview, not draft
  status: z
    .enum(["preview", "published"])
    .default("preview")
    .describe("Publication status"),
});

// Model Schema - Simplified for Zod v4 compatibility
export const ModelSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  area_id: z.string(),
  slug: z.string(),
  coordinates: z
    .object({
      type: z.literal("Point"),
      coordinates: z.tuple([z.number(), z.number()]),
    })
    .nullable()
    .optional(),
  model_reference_3d: z
    .union([
      // Pending state: has original only (before processing)
      z.object({
        original: z.object({
          url: z.string(),
          size: z.number(),
          sizeReadable: z.string(),
        }),
      }),
      // Processed state: has variants only (after processing)
      z.object({
        variants: z.record(z.string(), z.unknown()),
      }),
    ])
    .nullable()
    .optional(),
  images: z.array(ImageSchema).nullable().optional(),

  status: z.enum(["preview", "published"]).default("preview"),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  created_by: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  published_at: z.string().optional(),
});

// Route Schema
export const RouteSchema = z
  .object({
    id: z.uuid().describe("Unique identifier for the route"),
    model_id: z.uuid().describe("Foreign key linking to the parent Model"),
    name: z.string().min(1).describe("Name or identifier of the route"),
    difficulty_rating: z
      .number()
      .int()
      .min(0)
      .max(22)
      .describe("Universal difficulty rating (0-22 scale)"),
    description: z
      .string()
      .nullable()
      .optional()
      .describe("Detailed explanation of the route"),
    line_data_3d: z
      .array(z.array(z.number()))
      .nullable()
      .optional()
      .describe(
        "List of 3D line data (array of flat [x,y,z] positions for each line segment)"
      ),
    metadata: z
      .record(z.string(), z.unknown())
      .nullable()
      .optional()
      .describe("Additional details like tags or user notes"),
    placement: z
      .object({
        version: z.string(),
        computedAt: z.string().datetime(),
        routeMidpoint: z.tuple([z.number(), z.number(), z.number()]),
        labelPosition: z.tuple([z.number(), z.number(), z.number()]),
        outwardDirection: z.tuple([z.number(), z.number(), z.number()]),
        camera: z.object({
          position: z.tuple([z.number(), z.number(), z.number()]),
          target: z.tuple([z.number(), z.number(), z.number()]),
          yaw: z.number(),
          pitch: z.number(),
          distance: z.number(),
        }),
      })
      .nullable()
      .optional()
      .describe("Precomputed label/camera placement (versioned JSON)"),
    total_ascents: z
      .number()
      .int()
      .min(0)
      .optional()
      .describe("Total number of ascents for this route"),
    created_at: z.iso.datetime().describe("Timestamp of creation"),
    updated_at: z.iso.datetime().describe("Timestamp of last update"),
  })
  .describe("Represents a climbing route on a model");

// Schemas for request bodies (omitting read-only fields like id, created_at, updated_at)
export const CreateAreaSchema = AreaSchema.omit({
  id: true,
  created_by: true,
  created_at: true,
  updated_at: true,
  published_at: true,
});
export const UpdateAreaSchema = CreateAreaSchema.partial();

// Use the base schema for omit operations, then apply validation in the API layer
const CreateModelBase = BaseModelSchema.omit({
  id: true,
  created_by: true,
  created_at: true,
  updated_at: true,
  published_at: true,
});

export const CreateModelSchema = CreateModelBase;

export const UpdateModelSchema = CreateModelBase.partial();

export const CreateRouteSchema = RouteSchema.omit({
  id: true,
  created_at: true,
  updated_at: true,
});
export const UpdateRouteSchema = CreateRouteSchema.partial();

// Model Note Schema
export const ModelNoteSchema = z
  .object({
    id: z.uuid().describe("Unique identifier for the note"),
    model_id: z.uuid().describe("Foreign key linking to the parent Model"),
    user_id: z
      .string()
      .min(1)
      .describe("Firebase user ID who created the note"),
    content: z.string().min(1).describe("Note content/description text"),
    position_3d: z
      .object({
        x: z.number().describe("X coordinate in 3D world space"),
        y: z.number().describe("Y coordinate in 3D world space"),
        z: z.number().describe("Z coordinate in 3D world space"),
      })
      .describe("3D world coordinates where note is anchored"),
    color: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .default("#FFEB3B")
      .describe("Hex color for note marker display"),
    metadata: z
      .record(z.string(), z.unknown())
      .nullable()
      .optional()
      .describe("Additional metadata for future extensibility"),
    created_at: z.iso.datetime().describe("Timestamp of creation"),
    updated_at: z.iso.datetime().describe("Timestamp of last update"),
  })
  .describe(
    "Represents a private user note anchored to a 3D position on a model"
  );

// Schemas for note request bodies
export const CreateModelNoteSchema = ModelNoteSchema.omit({
  id: true,
  created_at: true,
  updated_at: true,
});
export const UpdateModelNoteSchema = CreateModelNoteSchema.partial().extend({
  id: z.uuid().describe("Note ID for the update"),
});

// Schema for fetching notes by model
export const GetModelNotesSchema = z
  .object({
    model_id: z.uuid().describe("Model ID to fetch notes for"),
    include_private: z
      .boolean()
      .optional()
      .default(false)
      .describe("Include private notes (only works for authenticated user)"),
  })
  .describe("Parameters for fetching model notes");

export const ConceptTypeSchema = z
  .enum([
    "areas",
    "models",
    "routes",
    "general",
    "profile_image",
    "social-post",
  ])
  .describe("Type of content the media is associated with");

export const GetSignedUrlRequestSchema = z
  .object({
    conceptType: ConceptTypeSchema,
    conceptId: z.uuid().describe("UUID of the associated content item"),
    filename: z.string().min(1).describe("Original filename of the upload"),
    contentType: z
      .string()
      .min(1)
      .describe("MIME type of the file (e.g., image/jpeg)"),
    extension: z
      .string()
      .min(1)
      .describe("File extension (e.g., jpg, png, glb)"), // Add extension field
  })
  .describe("Request body for generating a signed upload URL");

export const GetSignedUrlResponseSchema = z
  .object({
    signedUrl: z.url().describe("The V4 signed URL for the PUT upload"),
    destinationPath: z
      .string()
      .min(1)
      .describe("The GCS path where the file will be stored"),
    bucketName: z.string().min(1).describe("The name of the GCS bucket"),
    processingMetadata: z.record(z.string(), z.unknown()).optional(),
  })
  .describe("Response body containing the signed upload URL");

// Search schemas
export const ContentSearchSchema = z
  .object({
    query: z.string().min(1).max(100).describe("Search query string"),
    types: z
      .array(z.enum(["areas", "models", "routes"]))
      .optional()
      .default(["areas", "models", "routes"])
      .describe("Content types to search within"),
    limit: z
      .number()
      .int()
      .min(1)
      .max(100)
      .optional()
      .default(20)
      .describe("Maximum number of results to return"),
    offset: z
      .number()
      .int()
      .min(0)
      .optional()
      .default(0)
      .describe("Number of results to skip for pagination"),
  })
  .describe("Query parameters for content search");

export const SearchResultSchema = z
  .object({
    id: z.uuid().describe("Unique identifier of the content"),
    type: z.enum(["area", "model", "route"]).describe("Type of content"),
    name: z.string().describe("Name of the content"),
    description: z
      .string()
      .nullable()
      .optional()
      .describe("Description of the content"),
    relevance: z
      .number()
      .describe("Relevance score for search ranking (internal use only)"),
    images: z
      .array(SearchImageSchema)
      .nullable()
      .optional()
      .describe("Collection of image variants"),
    parent_name: z
      .string()
      .nullable()
      .optional()
      .describe("Name of the parent content"),
    parent_id: z
      .string()
      .uuid()
      .nullable()
      .optional()
      .describe("ID of the parent content"),
    parent_type: z
      .string()
      .nullable()
      .optional()
      .describe("Type of the parent content"),
    // Hierarchical context for URL generation
    area_id: z
      .string()
      .uuid()
      .nullable()
      .optional()
      .describe("ID of the area in the hierarchy"),
    slug: z.string().optional().describe("URL-friendly identifier"),
    area_slug: z.string().optional().describe("Slug of the parent area"),
    model_slug: z.string().optional().describe("Slug of the parent model"),
  })
  .describe("Individual search result");

export const ContentSearchResultsSchema = z
  .object({
    results: z.array(SearchResultSchema).describe("Array of search results"),
    total: z.number().int().min(0).describe("Total number of matching results"),
    query: z.string().describe("The original search query"),
    limit: z.number().int().describe("Number of results requested"),
    offset: z.number().int().describe("Number of results skipped"),
  })
  .describe("Search results response");

// Query parameter schema for URL parsing (handles string-to-type conversion)
export const ContentSearchQuerySchema = z
  .object({
    query: z.string().min(1).max(100).describe("Search query string"),
    types: z
      .string()
      .optional()
      .transform((val) => {
        if (!val) return ["areas", "models", "routes"];
        const types = val
          .split(",")
          .filter((t) => ["areas", "models", "routes"].includes(t));
        if (types.length === 0) return ["areas", "models", "routes"];
        return types;
      })
      .describe("Comma-separated content types to search within"),
    limit: z
      .string()
      .optional()
      .transform((val) => {
        const num = val ? parseInt(val, 10) : 20;
        return Number.isNaN(num) ? 20 : Math.max(1, Math.min(100, num));
      })
      .describe("Maximum number of results to return"),
    offset: z
      .string()
      .optional()
      .transform((val) => {
        const num = val ? parseInt(val, 10) : 0;
        return Number.isNaN(num) ? 0 : Math.max(0, num);
      })
      .describe("Number of results to skip for pagination"),
  })
  .describe("Query parameters for content search (URL string format)");

// Array schemas for collections
export const AreasArraySchema = z.array(AreaSchema);
export const ModelsArraySchema = z.array(ModelSchema);
export const RoutesArraySchema = z.array(RouteSchema);
